import gsap from "gsap/all";
import { Container, filters, Sprite, WRAP_MODES } from "pixi.js";
import { singleton } from "tsyringe";
import { AppState } from "~/src/app-state";
import { paintingsData } from "~/src/config/paintings";
import { EventBus } from "~/src/event-bus";
import { PixiApp } from "~/src/pixi-app";
import { DraggerComponent } from "./dragger";
import { PaintingComponent } from "./painting";

const OFFSET = 0;
const DISP_SCALE = 100;
const DISP_ENTER_SCALE = 150;
const DISP_PAD = 100;
const DISP_Y_SPEED = 0.25;
const DISP_X_SPEED = .75;
const DEFAULT_DISP_X_SPEED = 0.1;
const DRAG_POS_UPDATE_DURATION = 0.7;

enum EScrollDir {
  NONE,
  LEFT,
  RIGHT,
}
@singleton()
export class PaintingsSliderComponent {
  private xScrollSpeed = DEFAULT_DISP_X_SPEED;
  private prevScrollPos: number;
  private container: Container = new Container();
  private paintingsContainer: Container = new Container();
  private paintings: PaintingComponent[] = [];
  private dispSprite: Sprite;
  private dispFilter: any;
  private dragTween: gsap.core.Tween;
  private xScrollSpeedTween: gsap.core.Tween;
  private alphaFilter: any;
  constructor(
    private pixiApp: PixiApp,
    private draggerComponent: DraggerComponent,
    private eventBus: EventBus,
    private appState: AppState
  ) {}
  mount() {
    const { container, paintingsContainer } = this;
    this.createAlphaFilter();
    container.filters = [this.alphaFilter];
    this.pixiApp.stage.addChild(container);
    container.addChild(paintingsContainer);
    this.draggerComponent.mount(container);
    this.createPaintings();
    this.createDisplacementFilter();
    this.setupEvents();
  }
  async enter() {
    this.enableInteraction();
    this.updateAllPaintingsVerticalPos();
    await this.playEnterAnimation();
  }
  async exit() {
    this.disableInteraction();
    await this.playExitAnimation();
    this.appState.isSliderVisited = true;
  }
  playEnterAnimation() {
    return new Promise((res) => {
      this.pixiApp.ticker.add(this.onTick);
      const tl = gsap.timeline();
      tl.to(this.alphaFilter, {
        alpha: 1,
        ease: "Expo.easeIn",
        duration: 1,
        onComplete: () => {
          this.draggerComponent.playEnterAnimation();
        },
      }).to(this.dispFilter.scale, {
        x: DISP_SCALE,
        y: DISP_SCALE,
        ease: "ease",
        duration: 4.5,
        onComplete: res,
      });
    });
  }
  playExitAnimation() {
    return new Promise((res) => {
      gsap.to(this.alphaFilter, {
        alpha: 0,
        onComplete: () => {
          this.pixiApp.ticker.remove(this.onTick);
          this.draggerComponent.nilGlowStrength();
          res(null);
        },
        duration: 0.5,
      });
    });
  }
  disableInteraction() {
    this.paintings.forEach((painting) => {
      painting.sprite.interactive = false;
    });
    this.draggerComponent.disableInteraction();
  }
  enableInteraction() {
    this.draggerComponent.enableInteraction();
    this.paintings.forEach((painting) => {
      painting.sprite.interactive = true;
    });
  }
  private createDisplacementFilter() {
    const sprite = new Sprite(this.pixiApp.loader.resources.textDisp.texture!);
    const { paintingsContainer } = this;
    paintingsContainer.addChild(sprite);
    sprite.texture.baseTexture.wrapMode = WRAP_MODES.REPEAT;
    const filter = new filters.DisplacementFilter(sprite);
    this.dispFilter = filter;
    this.dispSprite = sprite;
    filter.padding = DISP_PAD;
    filter.scale.set(DISP_ENTER_SCALE, DISP_ENTER_SCALE);
    paintingsContainer.filters = [filter];
  }
  private createAlphaFilter() {
    const alpha = new filters.AlphaFilter(0);
    alpha.padding = 100;
    this.alphaFilter = alpha;
  }
  private onTick = () => {
    const ySpeed = DISP_Y_SPEED;
    this.dispSprite.x += this.xScrollSpeed;
    this.dispSprite.y += ySpeed;
  };
  private createPaintings() {
    const paintings = paintingsData.map((data) => {
      const painting = new PaintingComponent(
        data,
        this.pixiApp.loader.resources[data.key + "thumb"].texture!,
        this.eventBus
      );
      painting.mount(this.paintingsContainer);
      painting.sprite.x = 0;
      this.setPaintingVerticalPos(painting);
      return painting;
    });
    this.paintings = paintings;
    this.setAllPaintingsHorizontalPos();
  }
  private setAllPaintingsHorizontalPos() {
    this.paintings.reduce((acc, painting) => {
      painting.sprite.x = acc;
      acc += painting.sprite.width + OFFSET;
      return acc;
    }, OFFSET);
    this.paintingsContainer.x = -(
      this.paintingsContainer.width / 2 -
      document.documentElement.clientWidth / 2 +
      OFFSET
    );
    this.prevScrollPos = this.paintingsContainer.x;
  }
  private setPaintingVerticalPos(painting: PaintingComponent) {
    // place paintings at the center
    // if (this.appState.isSliderVisited) {
    //   painting.sprite.y =
    //     document.documentElement.clientHeight / 2 - painting.sprite.height / 2;
    // } else {
    //   // at the bottom
    //   painting.sprite.y =
    //     document.documentElement.clientHeight - painting.sprite.height;
    // }
    painting.sprite.y =
      document.documentElement.clientHeight / 2 - painting.sprite.height / 2;
  }
  private updateAllPaintingsVerticalPos() {
    this.paintings.forEach((painting) => this.setPaintingVerticalPos(painting));
  }
  private setupEvents() {
    this.eventBus.on("resize", () => {
      this.paintings.forEach((painting) => {
        painting.updateScale();
        painting.sprite.x = 0;
        this.setPaintingVerticalPos(painting);
      });
      this.setAllPaintingsHorizontalPos();
    });
    this.eventBus.on("dragger/progress", this.onDrag);
  }
  private onDrag = (progress: number) => {
    const width =
      this.paintingsContainer.width -
      document.documentElement.clientWidth +
      OFFSET * 2;
    const x = -(width * progress);
    this.dragTween?.kill();
    this.dragTween = gsap.to(this.paintingsContainer, {
      x,
      duration: DRAG_POS_UPDATE_DURATION,
      onUpdate: this.onDragUpdate,
      onComplete: this.onDragComplete,
    });
  };
  private onDragComplete = () => {
    this.changeScrollDir(EScrollDir.NONE);
  };
  private changeScrollDir = (dir: EScrollDir) => {
    this.xScrollSpeedTween?.kill();
    let speed = dir === EScrollDir.NONE ? 0 : DISP_X_SPEED;
    switch (dir) {
      case EScrollDir.NONE:
        speed = DEFAULT_DISP_X_SPEED;
        break;
      case EScrollDir.LEFT:
        speed = DISP_X_SPEED * -1;
        break;
      case EScrollDir.RIGHT:
        speed = DISP_X_SPEED;
    }
    this.xScrollSpeedTween = gsap.to(this, {
      xScrollSpeed: speed,
      ease: "linear",
      duration: dir === EScrollDir.NONE ? 1.1 : 1,
    });
  };
  private onDragUpdate = () => {
    const prevScroll = this.prevScrollPos;
    const currentScroll = this.paintingsContainer.x;
    if (!prevScroll) {
      this.prevScrollPos = this.paintingsContainer.x;
    } else if (currentScroll > prevScroll) {
      this.changeScrollDir(EScrollDir.LEFT);
    } else if (currentScroll < prevScroll) {
      this.changeScrollDir(EScrollDir.RIGHT);
    } else {
      this.changeScrollDir(EScrollDir.NONE);
    }
    this.prevScrollPos = this.paintingsContainer.x;
  };
}
